import { ReportsSearchFilterType } from '@/components/search/SearchFilters';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import MagnifyingGlassLight from '@/assets/magnifyingGlassLight.svg';
import ArrowRightLight from '@/assets/arrowRightLight.svg';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { assignValuesFromRouteQuery, pushValuesToRouteQuery, replaceValuesOfRouteQuery, scrollIntoView } from '@/utils';
import SearchFilterOptions from '@/components/search/SearchFilterOptions';
import CheckboxBlankCircle from '@/assets/checkboxBlankCircle.svg';
import CheckboxCircle from '@/assets/checkboxCircle.svg';
import { ReportsSearchFacetsType } from '@/types';
import { ModalPortal } from '@/components';

enum SearchType {
  ALL = 'all',
  CATWALKS = 'catwalks',
}

enum OrderByType {
  SCORE = 'score',
  DATE = 'date',
}

type ImageLibraryFilterType = {
  filters: ReportsSearchFilterType[];
  facets?: ReportsSearchFacetsType[];
};

const ImageLibraryFilter = (data: ImageLibraryFilterType) => {
  const { filters, facets } = data;

  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState<SearchType>(SearchType.ALL);
  const [orderBy, setOrderBy] = useState<OrderByType>(OrderByType.SCORE);
  const [atTop, setAtTop] = useState(false);
  const [scrollingUp, setScrollingUp] = useState(false);
  const prevScrollPos = useRef(0);
  const atTopRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();
  const router = useRouter();

  useEffect(() => {
    const searchTermFromQuery = {
      searchTerm: '',
      searchType: SearchType.ALL,
      orderBy: OrderByType.SCORE,
    };
    assignValuesFromRouteQuery(router, searchTermFromQuery);
    setSearchTerm(searchTermFromQuery.searchTerm || '');
    setSearchType(searchTermFromQuery.searchType || SearchType.ALL);
    setOrderBy(searchTermFromQuery.orderBy || OrderByType.SCORE);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          setAtTop(true);
        } else {
          setAtTop(false);
        }
      },
      {
        // root: columsContainer.current?.parentElement?.parentElement, // scroll container
        rootMargin: '50px', // preload before exact bottom
        threshold: 0,
      },
    );

    if (atTopRef.current) {
      observer.observe(atTopRef.current);
    }

    return () => {
      if (atTopRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(atTopRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const currentScrollPos = window.scrollY;
      if (currentScrollPos > prevScrollPos.current + 10) {
        setScrollingUp(false);
      } else if (currentScrollPos < prevScrollPos.current || window.scrollY === 0) {
        setScrollingUp(true);
      }

      prevScrollPos.current = currentScrollPos;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    if (atTopRef.current && !atTop) {
      scrollIntoView(atTopRef.current, -200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query]);

  const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    requestAnimationFrame(() => {
      pushValuesToRouteQuery(router, { searchTerm });
    });
  };

  const clearSearchTerm = () => {
    setSearchTerm('');
    pushValuesToRouteQuery(router, { searchTerm: '' });
  };

  const _setSearchType = (type: SearchType) => {
    if (type === searchType) return;
    setSearchType(type);
    if (type === SearchType.ALL) {
      replaceValuesOfRouteQuery(router, { orderBy: router.query.orderBy, searchTerm: router.query.searchTerm });
    } else {
      pushValuesToRouteQuery(router, { searchType: type === SearchType.CATWALKS ? type : null });
    }
  };

  const _setOrderBy = (_orderBy: OrderByType) => {
    if (_orderBy === orderBy) return;
    setOrderBy(_orderBy);
    pushValuesToRouteQuery(router, { orderBy: _orderBy === OrderByType.DATE ? _orderBy : null });
  };

  const content = (
    <div className="mx-auto w-full lg:w-10/12">
      <form
        onSubmit={handleSubmit}
        className="border-gray-5 flex w-full items-center rounded-full border bg-white p-1 md:p-2.5"
      >
        <MagnifyingGlassLight className="mr-2 h-6 w-6 shrink-0 fill-gray-400" />
        <input
          type="text"
          name="searchTerm"
          className="w-full bg-transparent text-base outline-none placeholder:text-gray-400"
          value={searchTerm}
          placeholder={t('imageLibrary.$searchImages')}
          autoComplete="off"
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {searchTerm && (
          <button type="button" onClick={() => clearSearchTerm()} className="btn icon mr-3">
            {t('general.$clear')}
          </button>
        )}

        <button type="button" onClick={() => handleSubmit()} className="btn icon !bg-[#E63946] hover:!bg-[#f36772]">
          <ArrowRightLight className="h-5 w-5 !fill-white" />
        </button>
      </form>

      <div className="flex w-full flex-col justify-between gap-2 pt-2 md:flex-row md:px-3">
        <div className="flex w-full flex-col items-center gap-2 md:flex-row md:items-start lg:gap-4">
          <div className="border-gray-4 flex rounded-full border bg-white">
            <button
              onClick={() => _setSearchType(SearchType.ALL)}
              className={classNames(
                '-mt-px -mb-px -ml-px flex-1 cursor-pointer rounded-full border px-4 py-1.5 text-sm whitespace-nowrap transition-colors',
                searchType === SearchType.ALL ? 'border-[#E63946] bg-[#E63946] text-white' : 'border-transparent',
              )}
            >
              {t('imageLibrary.$allImages')}
            </button>
            <button
              onClick={() => _setSearchType(SearchType.CATWALKS)}
              className={classNames(
                '-mt-px -mr-px -mb-px flex-1 cursor-pointer rounded-full border px-4 py-1.5 text-sm whitespace-nowrap transition-colors',
                searchType === SearchType.CATWALKS ? 'border-[#E63946] bg-[#E63946] text-white' : 'border-transparent',
              )}
            >
              {t('imageLibrary.$catwalks')}
            </button>
          </div>
          {searchType === SearchType.CATWALKS && (
            <div>
              <SearchFilterOptions
                availableFilters={filters}
                facets={facets}
                isPublic={false}
                className="[&_.main-button]:!border-gray-4 lg:!gap-4 [&_.main-button]:!h-8.5 [&_.main-button]:bg-white"
                clearBtnLabel={t('imageLibrary.$clearfilter')}
              />
            </div>
          )}
        </div>
        <div className="flex items-start justify-center gap-2 lg:gap-4">
          <button
            className="btn border-gray-4 border bg-white !py-0.5 !pr-2 !pl-0.5 !font-medium whitespace-nowrap hover:bg-gray-100 disabled:!text-gray-400 disabled:hover:bg-white"
            onClick={() => _setOrderBy(OrderByType.SCORE)}
          >
            {orderBy === OrderByType.SCORE ? (
              <CheckboxCircle className="h-7 w-7" />
            ) : (
              <CheckboxBlankCircle className="!stroke-gray-4 h-7 w-7 !fill-white" />
            )}

            <span className="mt-px mb-0.5 text-sm">{t('imageLibrary.$relevance')}</span>
          </button>
          <button
            className="btn border-gray-4 border bg-white !py-0.5 !pr-2 !pl-0.5 !font-medium whitespace-nowrap hover:bg-gray-100 disabled:!text-gray-400 disabled:hover:bg-white"
            onClick={() => _setOrderBy(OrderByType.DATE)}
          >
            {orderBy === OrderByType.DATE ? (
              <CheckboxCircle className="h-7 w-7" />
            ) : (
              <CheckboxBlankCircle className="!stroke-gray-4 h-7 w-7 !fill-white" />
            )}

            <span className="mt-px mb-0.5 text-sm">{t('event.eventCalendarFilters.$date')}</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {content}
      <div ref={atTopRef} className="h-10 w-full" />

      <ModalPortal>
        <div
          className={classNames(
            'scrollbar-placeholder fixed top-0 hidden w-full px-6 transition-transform md:block lg:px-0',
            !atTop && scrollingUp ? 'md:translate-y-20' : 'overflow-hidden md:-translate-y-40',
          )}
        >
          {content}
        </div>
      </ModalPortal>
    </>
  );
};

export default ImageLibraryFilter;
